// Pagination uses real Next.js navigation, while filter toggles in this same
// search experience use history.pushState via useUpdateVehiclesSearchUrl. The
// asymmetry is intentional and matches the interaction patterns:
//
//   - Pagination: low URL cardinality (page 1, 2, 3…), high Cloudflare cache
//     hit rate, and natural dwell time between clicks. Viewport prefetch warms
//     the RSC payload before the click, so a real <Link> navigation feels
//     instant while letting the server stay the canonical source of truth
//     (one code path for crawlers and users, automatic metadata refresh, no
//     manual canonical sync needed for this case).
//
//   - Filters: high URL cardinality (long-tail combinations), low cache hit
//     rate, and rapid successive clicks where prefetch can't keep up. pushState
//     gives an instant URL update plus a targeted /api/search/vehicles fetch,
//     and we manually sync title/canonical (see SearchContainer) since
//     generateMetadata doesn't re-run on pushState.
//
// Scroll is handled here (not by next/link's auto-scroll) so we land on the
// utility bar with a smooth animation rather than an instant jump — image
// loading masks the small navigation gap nicely.

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import {
  PaginationEllipsis,
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { createPaginationWithEllipsis } from '@//utils/pagination';
import { facetParamsToPath, sanitizeParams } from '@//utils/vehicles-search/utils';

const scrollToUtilityBar = () => {
  // Instant scroll: the results are already in the DOM (SSR + initialData), so
  // there's no loading state to mask. A smooth animation would just delay the
  // landing position without adding information.
  document.getElementById('search-utility-bar')?.scrollIntoView({
    behavior: 'instant',
    block: 'start',
  });
};

const resolveHref = (rootUrl: string, currentPathname: string, q?: string) => {
  const params = sanitizeParams(new URLSearchParams(q), { keepOffset: true });
  const hasOffset = params.has('offset');
  const hasQ = params.has('q');
  if (hasOffset || hasQ) {
    return `${rootUrl}${params.toString() ? `?${params}` : ''}`;
  }

  const pretty = facetParamsToPath(params);

  const paramsAsString = params.toString();
  const href = pretty
    ? `${rootUrl}/${pretty}`
    : paramsAsString
      ? `?${paramsAsString}`
      : currentPathname;

  return href;
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const pathname = usePathname();

  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.target as HTMLElement).blur();
    scrollToUtilityBar();
    document.getElementById('search-results')?.focus({ preventScroll: true });
  }, []);

  const prevQuery = pagination?.previousPage?.query;
  const nextQuery = pagination?.nextPage?.query;

  const paginationItems = useMemo(
    () => createPaginationWithEllipsis(pagination?.pages || []),
    [pagination?.pages],
  );

  if (!pagination) {
    return null;
  }

  return (
    <StyledPagination testId="search-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prevQuery ? resolveHref(rootUrl, pathname, prevQuery) : undefined}
        testId="search-pagination-prev"
        onClick={prevQuery ? onClick : undefined}
        scroll={false}
      />
      {paginationItems.map((item, index) => {
        if (item.isEllipsis) {
          return (
            <PaginationEllipsis
              key={`ellipsis-${index}`}
              testId={`search-pagination-ellipsis-${index}`}
            />
          );
        }

        return (
          <PaginationLink
            key={item.displayName}
            href={resolveHref(rootUrl, pathname, item.query!)}
            onClick={onClick}
            title={t('goTo.title', { page: item.displayName })}
            testId={`search-pagination-page-${item.displayName}`}
            isActive={item.selected}
            scroll={false}
          >
            {item.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={nextQuery ? resolveHref(rootUrl, pathname, nextQuery) : undefined}
        testId="search-pagination-next"
        onClick={nextQuery ? onClick : undefined}
        scroll={false}
      />
    </StyledPagination>
  );
};

export default Pagination;
